import { DEFAULT_SETTINGS, type TenantSettings } from '@dairy/shared';
import { dryOffDue, expectedCalving } from './breeding';
import { eventsOf, isAfter, latestOnOrBefore } from './events';
import type { DomainAnimal, DomainEvent } from './types';

export type ReproStatus = 'OPEN' | 'SERVED' | 'PREGNANT';

export interface ReproInfo {
  status: ReproStatus;
  /** Set when PREGNANT (D-2 rule 3). */
  conceivingServiceId?: string;
  lastServiceId?: string;
  lastServiceDate?: string;
  lastCalvingDate?: string;
  expectedCalving?: string;
  dryOffDue?: string;
}

/** Events of the current breeding cycle on `date`: after the latest calving on or before it (or since birth). */
export function cycleEvents(sortedAnimalEvents: readonly DomainEvent[], date: string): {
  calving: DomainEvent | undefined;
  events: DomainEvent[];
} {
  const calving = latestOnOrBefore(sortedAnimalEvents, 'CALVING', date);
  const events = sortedAnimalEvents.filter((e) => e.date <= date && (!calving || isAfter(e, calving)));
  return { calving, events };
}

/** D-2 Reproductive status on `date` (females). Returns null for males. */
export function deriveReproStatus(
  animal: DomainAnimal,
  events: readonly DomainEvent[],
  date: string,
  s: Pick<TenantSettings, 'gestationDays' | 'dryPeriodDays'> = DEFAULT_SETTINGS,
): ReproInfo | null {
  if (animal.sex !== 'FEMALE') return null;
  const evs = eventsOf(events, animal.id);
  const { calving, events: cycle } = cycleEvents(evs, date);
  const base: Pick<ReproInfo, 'lastCalvingDate'> = calving ? { lastCalvingDate: calving.date } : {};
  const services = cycle.filter((e) => e.type === 'SERVICE');
  const S = services[services.length - 1];
  if (!S) return { status: 'OPEN', ...base };
  const svc = { lastServiceId: S.id, lastServiceDate: S.date, ...base };
  const after = cycle.filter((e) => isAfter(e, S));
  if (after.some((e) => e.type === 'ABORTION')) return { status: 'OPEN', ...svc };
  const checks = after.filter((e) => e.type === 'PREG_CHECK');
  const latestCheck = checks[checks.length - 1];
  if (latestCheck) {
    if ((latestCheck.data as { result?: string }).result === 'PREGNANT') {
      const exp = expectedCalving(S.date, s);
      return {
        status: 'PREGNANT',
        conceivingServiceId: S.id,
        expectedCalving: exp,
        dryOffDue: dryOffDue(exp, s),
        ...svc,
      };
    }
    return { status: 'OPEN', ...svc };
  }
  if (after.some((e) => e.type === 'HEAT')) return { status: 'OPEN', ...svc };
  return { status: 'SERVED', ...svc };
}

/** BR-3: does the cow have a service since her last calving (or birth), on or before `date`? */
export function hasServiceInCycle(animal: DomainAnimal, events: readonly DomainEvent[], date: string): boolean {
  const { events: cycle } = cycleEvents(eventsOf(events, animal.id), date);
  return cycle.some((e) => e.type === 'SERVICE');
}
